import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);
import { Card, Input, Select, Table } from "@stonelog/stonelog-design-system";
import { DatePickerCustom } from "@components/DatePickerCustom";
import { usePaginationConfig } from "@hooks/usePaginationConfig";
import { GingaIcon } from "@stonelog/stonelog-ginga-icons";
import { compareTableColumn } from "../../../../utils/common";

import { hubsSelector } from "../../../../features/senninha/hubsSlice";
import { getAttachmentOs, attachmentSelector, resetAttachmentOsTableData } from "../../../../features/anexos/attachmentSlice";
import AttachmentServiceOrderDrawer from "./AttachmentServiceOrderDrawer";

import * as S from "./styles";

const AttachmentServiceOrder = () => {
    const dispatch = useDispatch();

    const { allHubs, allHubsStatus } = useSelector(hubsSelector);
    const { getAttachmentOsTableData, getAttachmentOsTableDataStatus } = useSelector(attachmentSelector);

    const [poloSelected, setPoloSelected] = useState(null);
    const [dateSelected, setDateSelected] = useState(null);
    const [osSelected, setOsSelected] = useState(null);
    const [customerSelected, setCustomerSelected] = useState(null);
    const [angelSelected, setAngelSelected] = useState(null);
    const [searchOsInput, setSearchOsInput] = useState("");

    const [open, setOpen] = useState(false);
    const [rowClickedInfos, setRowClickedInfos] = useState({});

    const paginationConfig = usePaginationConfig([10, 10]);

    useEffect(() => {
        dispatch(resetAttachmentOsTableData());
    }, [dispatch]);

    useEffect(() => {
        if (dateSelected && poloSelected) {
            const formattedDate = dateSelected.format("YYYY-MM-DD");

            const selectedPoloObj = allHubs?.find(p => p.id === poloSelected);
            const poloName = selectedPoloObj ? selectedPoloObj.name : null;

            if (poloName) {
                setOsSelected(null);
                setCustomerSelected(null);
                setAngelSelected(null);
                setSearchOsInput("");

                dispatch(getAttachmentOs({
                    dataOs: formattedDate,
                    poloName: poloName
                }));
            }
        }
    }, [dispatch, dateSelected, poloSelected, allHubs]);

    const handleSearchByOs = () => {
        const trimmed = searchOsInput.trim();
        if (!trimmed) return;

        setPoloSelected(null);
        setDateSelected(null);
        setOsSelected(null);
        setCustomerSelected(null);
        setAngelSelected(null);

        dispatch(getAttachmentOs({ orderNumber: trimmed }));
    };

    const columns = [
        {
            title: 'Número da OS',
            dataIndex: 'os',
            key: 'os',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "os"),
            },
        },
        {
            title: 'Polo',
            dataIndex: 'polo',
            key: 'polo',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "polo"),
            },
        },
        {
            title: "Nome do estabelecimento",
            dataIndex: "nomeEstabelecimento",
            key: "nomeEstabelecimento",
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "nomeEstabelecimento"),
            },
        },
        {
            title: 'Angel',
            dataIndex: 'angel',
            key: 'angel',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "angel"),
            },
        },
        {
            title: 'Data de baixa',
            dataIndex: 'dataBaixa',
            key: 'dataBaixa',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "dataBaixa"),
            },
        },
    ];

    const osOptions = useMemo(() => {
        if (!getAttachmentOsTableData || getAttachmentOsTableData.length === 0) return [];

        let filteredData = getAttachmentOsTableData;

        if (customerSelected) {
            filteredData = filteredData.filter(item => item.customer === customerSelected);
        }

        const uniqueOsNumbers = [...new Set(filteredData.map(item => item.order_number))];

        return uniqueOsNumbers.map(os => ({
            value: os,
            label: String(os)
        }));
    }, [getAttachmentOsTableData, customerSelected]);

    const customerOptions = useMemo(() => {
        if (!getAttachmentOsTableData || getAttachmentOsTableData.length === 0) return [];

        const uniqueCustomers = [];
        const seen = new Set();

        getAttachmentOsTableData.forEach(item => {
            if (item.customer && !seen.has(item.customer)) {
                seen.add(item.customer);

                uniqueCustomers.push({
                    value: item.customer,
                    label: String(item.customer)
                });
            }
        });

        return uniqueCustomers;
    }, [getAttachmentOsTableData]);

    const angelOptions = useMemo(() => {
        if (!getAttachmentOsTableData || getAttachmentOsTableData.length === 0) return [];

        const uniqueAngels = [];
        const seen = new Set();

        getAttachmentOsTableData.forEach(item => {
            if (item.technician_name && !seen.has(item.technician_name)) {
                seen.add(item.technician_name);

                uniqueAngels.push({
                    value: item.technician_name,
                    label: String(item.technician_name)
                });
            }
        });

        return uniqueAngels;
    }, [getAttachmentOsTableData]);

    const dataSource = useMemo(() => {
        if (!getAttachmentOsTableData || getAttachmentOsTableData.length === 0) return [];

        let filteredData = getAttachmentOsTableData;

        if (osSelected) {
            filteredData = filteredData.filter(item => item.order_number === osSelected);
        }

        if (customerSelected) {
            filteredData = filteredData.filter(item => item.customer === customerSelected);
        }

        if (angelSelected) {
            filteredData = filteredData.filter(item => item.technician_name === angelSelected);
        }

        return filteredData.map((item, idx) => ({
            key: idx,
            os: item.order_number,
            polo: item.provider,
            nomeEstabelecimento: item.customer_name,
            angel: item.technician_name,
            dataBaixa: item.arrival_date ? dayjs(item.arrival_date).format('DD/MM/YYYY') : '-',
        }));
    }, [getAttachmentOsTableData, osSelected, customerSelected, angelSelected]);

    const onChangeDate = (date) => {
        setDateSelected(date);
    };

    const poloOptions = useMemo(() => {
        if (!allHubs || allHubs.length === 0) return [];

        return allHubs.map(polo => ({
            value: polo.id,
            label: polo.name
        }));
    }, [allHubs]);

    return (
        <>
            <AttachmentServiceOrderDrawer
                open={open}
                setOpen={setOpen}
                rowClickedInfos={rowClickedInfos}
            />
            <Card.Root>
                <Card.Body className="ginga-lib-card-body">
                    <Card.Header>
                        <Card.HeaderText
                            title="Anexos das Ordens de Serviço"
                            subtitle="Acompanhe os atendimentos e, para visualizar os detalhes do comprovante, clique na ordem de serviço desejada."
                        />
                    </Card.Header>
                    <S.InventaryFilterContainer>
                        <S.TopInventaryFilterContent>
                            <Input
                                size="small"
                                label="Buscar OS"
                                value={searchOsInput}
                                onChange={(e) => setSearchOsInput(e.target.value)}
                                onPressEnter={handleSearchByOs}
                                placeholder="Digite o número da OS"
                                clear
                            />
                            <Select
                                prefixIcon={<GingaIcon name="location" color="rgba(118, 127, 141, 1)" size={21} />}
                                placeholder="Selecione um polo"
                                label="Polo"
                                size="small"
                                value={poloSelected}
                                onChange={(val) => setPoloSelected(val)}
                                options={poloOptions}
                                disabled={allHubsStatus === 'pending'}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                className="ginga-lib-select-table"
                            />
                            <DatePickerCustom
                                label={"Data da Os"}
                                value={dateSelected}
                                onChange={onChangeDate}
                                format={"DD/MM/YYYY"}
                                placeholder={"DD/MM/AAAA"}
                                allowClear
                            />
                            <Select
                                placeholder="Filtrar por Angel"
                                label="Angel"
                                size="small"
                                value={angelSelected}
                                onChange={(val) => setAngelSelected(val)}
                                options={angelOptions}
                                disabled={!getAttachmentOsTableData || getAttachmentOsTableData.length === 0 || getAttachmentOsTableDataStatus === 'pending'}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                className="ginga-lib-select-table"
                                suffixIcon={<GingaIcon name="chevron-down" color="rgba(118, 127, 141, 1)" size={21} />}
                            />
                            <Select
                                placeholder="Filtrar por Cliente"
                                label="Cliente"
                                size="small"
                                value={customerSelected}
                                onChange={(val) => {
                                    setCustomerSelected(val);
                                    setOsSelected(null);
                                }}
                                options={customerOptions}
                                disabled={!getAttachmentOsTableData || getAttachmentOsTableData.length === 0 || getAttachmentOsTableDataStatus === 'pending'}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                className="ginga-lib-select-table"
                                suffixIcon={<GingaIcon name="chevron-down" color="rgba(118, 127, 141, 1)" size={21} />}
                            />
                        </S.TopInventaryFilterContent>
                    </S.InventaryFilterContainer>
                    <Table
                        paginationConfig={paginationConfig}
                        columns={columns}
                        dataSource={dataSource}
                        loading={getAttachmentOsTableDataStatus === 'pending'}
                        className="ginga-lib-table"
                        onRow={(row) => ({
                            onClick: () => {
                                setRowClickedInfos(row)
                                setOpen(true)
                            },
                        })}
                    />
                </Card.Body>
            </Card.Root>
        </>
    )
};

export default AttachmentServiceOrder;